import PropTypes from 'prop-types';

import { legacyTheme, styled } from '@prose-ui/legacy';
import { Formik } from 'formik';

import { Typography } from 'Components/Typography';

import Tray from 'Blocks/Tray';

import consultationCategoryProp from 'PropTypes/consultationProps';

import { getFragranceDetails } from 'utils/fragrances';

import QuestionLayout from './QuestionLayout';

const CardArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0;
  width: 100%;
`;

const Title = styled(Typography)`
  margin-top: ${legacyTheme.spacing.s24};
  margin-bottom: 0;
`;

const Fragrance = ({
  consultationCategory,
  fragrances,
  initialValue,
  isSaving,
  onNext,
  questionIndex,
  standalone,
  subtitle,
  title,
  isSoldOut,
}) => {
  const initialObject = fragrances.reduce(
    (acc, fragrance) => ({
      ...acc,
      [fragrance.name]: fragrance.name === initialValue,
    }),
    {}
  );

  return (
    <Formik initialValues={initialObject}>
      {({ values, setFieldValue }) => {
        const selectedList = fragrances.filter(fragrance => values[fragrance.name]);
        const selected = selectedList?.length ? selectedList[0].name : null;
        const cards = fragrances.map(fragrance => {
          const { ingredients, description, url, imageAlt, urlsByAssetName, name, slug, tooltip } =
            getFragranceDetails(fragrance.name);
          return {
            description,
            ingredients,
            tooltip,
            slug,
            category: fragrance.category,
            imageUrl: urlsByAssetName?.fragFreeBottleImg ?? url,
            imageAlt,
            label: name,
            selected: values[fragrance.name],
            hasSelection: Boolean(selected),
            recommended: fragrance.recommended,
            limitedEdition: fragrance.limited,
          };
        });
        const recommendedFragrances = cards
          .filter(c => c.category !== null)
          .filter(c => c.recommended);
        const nonRecommendedFragrances = cards
          .filter(c => c.category !== null)
          .filter(c => !c.recommended);

        // Unset all other values if setting
        const setValue = (fragrance, value) => {
          if (value && selected) {
            setFieldValue(selected, false);
          }
          selectedList.forEach(selectedListElement => setFieldValue(selectedListElement, false));
          setFieldValue(fragrance, value);
        };

        const onCardClick = slug => {
          const fragrance = slug;
          setValue(fragrance, !values[fragrance]);
        };

        const submit = () => onNext(selected);

        return (
          <QuestionLayout
            buttonLabel={standalone ? 'Save' : 'Next'}
            consultationCategory={consultationCategory}
            disabled={!selected}
            isSaving={isSaving}
            questionIndex={questionIndex}
            submit={submit}
            subtitle={subtitle}
            title={title}
          >
            {isSoldOut && (
              <Typography align="center" color="rouge" variant="p2">
                Your most recent fragrance choice is no longer available. Please choose a new
                fragrance:
              </Typography>
            )}

            <CardArea>
              <div>
                <Title align="center" variant="mono2">
                  Recommended Fragrances
                </Title>
                <Tray
                  CardProps={{ dataTestId: 'recommended-fragrance-card' }}
                  cards={recommendedFragrances}
                  noMaxWidth
                  onCardClick={onCardClick}
                />
              </div>
            </CardArea>

            <CardArea>
              <div>
                <Title align="center" variant="mono2">
                  Other Fragrances
                </Title>
                <Tray
                  CardProps={{ dataTestId: 'non-recommended-fragrance-card' }}
                  cards={nonRecommendedFragrances}
                  noMaxWidth
                  onCardClick={onCardClick}
                />
              </div>
            </CardArea>
          </QuestionLayout>
        );
      }}
    </Formik>
  );
};

Fragrance.propTypes = {
  consultationCategory: consultationCategoryProp.isRequired,
  fragrances: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialValue: PropTypes.string,
  isSaving: PropTypes.bool.isRequired,
  isSoldOut: PropTypes.bool,
  onNext: PropTypes.func.isRequired,
  questionIndex: PropTypes.number.isRequired,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  standalone: PropTypes.bool,
};

Fragrance.defaultProps = {
  initialValue: null,
  isSoldOut: false,
  standalone: false,
  subtitle: null,
};

export default Fragrance;
